import { db } from "@/common/lib/firebase/firebase"
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import {
    doc,
    getDoc,
    updateDoc,
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore"
import { auth } from "@/common/lib/firebase/firebase"
import { NavigateFunction } from "react-router-dom"
import { transferAccountOwnership } from "./authQueries"
import {
    sendOwnershipTransferRequestSubmittedToAdmins,
    sendOwnershipTransferSubmittedToNewOwner,
    type OwnershipTransferSubmittedToNewOwnerDTO,
} from "./mailService"

export interface AccountOwnershipTransferRequest {
    firstName: string
    lastName: string
    email: string
}

export interface UserData {
    acceptTCs: boolean
    charityNumber: string
    country: string
    dateCreated: string
    email: string
    firstName: string
    lastName: string
    mobileExtension: string
    mobileNumber: string
    secondaryFirstName: string
    secondaryLastName: string
    secondaryEmail: string
    role: string
    totalPastGrantAmount: null
    userId: string
    inactive?: boolean
    isAccountOwnershipTransferPending?: boolean
    accountOwnershipTransferRequest?: AccountOwnershipTransferRequest
}

export interface UserOwnerShipData
    extends Pick<UserData, "firstName" | "lastName" | "email"> {}

async function getUserById(_navFunc: NavigateFunction) {
    // Keep this query side-effect free. Route protection is handled by useAuth-based guards.
    // Redirecting from here can cause login<->dashboard loops when auth is valid but profile doc is missing.
    const id = auth.currentUser?.uid

    if (!id) {
        return null
    }

    const userDocRef = doc(db, "users", id)
    const userDocSnapshot = await getDoc(userDocRef)

    if (!userDocSnapshot.exists()) {
        return null
    }

    return userDocSnapshot.data() as UserData
}

export const useGetUserById = (navFunc: NavigateFunction) => {
    return useQuery({
        queryKey: ["userDetails"],
        queryFn: () => getUserById(navFunc),
        retry: false,
    })
}

export const useUpdateUserName = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            firstName,
            lastName,
        }: {
            firstName: string
            lastName: string
        }) => {
            const id = auth.currentUser?.uid
            if (!id) throw new Error("No auth")
            const ref = doc(db, "users", id)
            await updateDoc(ref, { firstName, lastName })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
        },
    })
}

export const useRequestAccountOwnershipTransfer = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            firstName,
            lastName,
            email,
        }: AccountOwnershipTransferRequest) => {
            const id = auth.currentUser?.uid
            if (!id) throw new Error("No auth")
            const ref = doc(db, "users", id)

            // Get current user data for orgName
            const userSnap = await getDoc(ref)
            const userData = userSnap.data() as UserData
            const orgName = userData?.charityNumber
                ? `Charity ${userData.charityNumber}`
                : "the organisation"

            await updateDoc(ref, {
                isAccountOwnershipTransferPending: true,
                accountOwnershipTransferRequest: { firstName, lastName, email },
            })

            // Notify admins
            await sendOwnershipTransferRequestSubmittedToAdmins({
                userId: id,
            }).catch((e) =>
                console.error("Failed to send admin notification:", e)
            )

            // Notify proposed new owner (Case 1)
            const newOwnerDto: OwnershipTransferSubmittedToNewOwnerDTO = {
                email,
                userName: `${firstName} ${lastName}`.trim(),
                orgName,
                loginUrl:
                    "https://app.christinamaryhendrietrust.co.uk/auth/login",
            }
            await sendOwnershipTransferSubmittedToNewOwner([newOwnerDto]).catch(
                (e) =>
                    console.error("Failed to send new owner notification:", e)
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
        },
    })
}

const getPendingAccountTransfers = async () => {
    const transfersQuery = query(
        collection(db, "users"),
        where("isAccountOwnershipTransferPending", "==", true)
    )
    const snapshot = await getDocs(transfersQuery)
    return snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as UserData),
        userId: docSnap.id,
    }))
}

export const useGetPendingAccountTransfers = () =>
    useQuery({
        queryKey: ["pendingAccountTransfers"],
        queryFn: getPendingAccountTransfers,
    })

export const useTransferAccountOwnership = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (dto: {
            userId: string
            action: "accept" | "reject"
        }) => {
            return transferAccountOwnership(dto)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["pendingAccountTransfers"],
            })
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
        },
    })
}
